import { ReactNode, useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import { EmptyStates } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import {
  Text,
  useControlledField,
  useFormField,
  SearchField,
  ChecklistField,
  useDebouncedValue,
  Accordion,
  useTooltip,
} from '@frontend/design-system';
import { useScheduleMultiSelectDropdownContext } from './ScheduleMultiSelectDropdownContext';

type ScheduleMultiSelectDropdownProps = {
  children: ReactNode;
};

export const ScheduleMultiSelectDropdown = ({ children }: ScheduleMultiSelectDropdownProps) => {
  const { t } = useTranslation('schedule');

  const {
    id,
    initialListValues,
    placeholder,
    values,
    defaultSearchValue,
    getNameFieldValue,
    setListValues,
    setValues,
    setIsError,
    isError,
    isRequired,
  } = useScheduleMultiSelectDropdownContext();

  const multiSelectChecklistProps = useControlledField({
    type: 'checklist',
    value: values,
    onChange: (values: string[]) => onChecklistFieldChange(values),
  });

  const searchFieldProps = useFormField(
    {
      type: 'text',
      value: defaultSearchValue || '',
    },
    [defaultSearchValue]
  );

  const searchValue = useDebouncedValue(searchFieldProps.value);

  const displayValues = useMemo<{ value: string; children: string }[]>(() => {
    if (Array.isArray(children)) {
      return children
        .filter((child) => {
          return values.includes(child.props.value);
        })
        .map((child) => child.props);
    }
    return [];
  }, [children, values]);

  // Filter list based on search value
  useEffect(() => {
    if (searchValue) {
      const filteredList = initialListValues.filter((item) => {
        const name = getNameFieldValue(item);
        return name?.toLowerCase().includes(searchValue.toLowerCase());
      });
      setListValues(filteredList);
    } else {
      setListValues(initialListValues);
    }
  }, [searchValue]);

  useEffect(() => {
    if (isRequired && values.length === 0) {
      setIsError(true);
    } else {
      setIsError(false);
    }
  }, [isRequired, values]);

  const onChecklistFieldChange = (checklistFieldValues: string[]) => {
    setValues(checklistFieldValues);
  };

  const onClear = (checklistFieldClearValue: string) => {
    setValues(values.filter((v) => v !== checklistFieldClearValue));
  };

  const hasError = !!isError && !!isRequired && values.length === 0;

  return (
    <>
      <Accordion
        css={accordionStyles(hasError)}
        padding={1}
        size='small'
        showBoxShadow={false}
        id={id}
        chevronSize={10}
      >
        <Accordion.Item value={'provider list'} css={{ border: `1px solid ${theme.colors.neutral10}` }}>
          <Accordion.Header css={{ border: `1px solid ${theme.colors.neutral10}` }}>
            {!displayValues.length ? (
              <Text color={hasError ? 'error' : 'light'}>{placeholder}</Text>
            ) : (
              <div css={{ display: 'flex', flexWrap: 'wrap', gap: theme.spacing(1) }}>
                {displayValues.map((displayValue) => (
                  <ScheduleMultiSelectDropdownChip
                    key={displayValue.value}
                    id={displayValue.value}
                    value={displayValue.children}
                    onClear={(value) => onClear(value)}
                  />
                ))}
              </div>
            )}
          </Accordion.Header>
          <Accordion.Body css={{ border: `1px solid ${theme.colors.neutral10}` }}>
            <SearchField name={t('search')} label={t('Search')} {...searchFieldProps} />
            {Array.isArray(children) && children.length === 0 ? (
              <EmptyStates
                emptyStateConfig={{
                  type: 'schedule',
                  header: `${t('No matches found')}`,
                  description: '',
                }}
              />
            ) : (
              <ChecklistField
                css={{ padding: theme.spacing(0.5), maxHeight: theme.spacing(30), overflow: 'scroll' }}
                {...multiSelectChecklistProps}
                name='multi-select'
                label=''
              >
                {children}
              </ChecklistField>
            )}
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
      {hasError && (
        <Text size='small' color='error'>
          {t('* This field is required')}
        </Text>
      )}
    </>
  );
};

const accordionStyles = (hasError: boolean) =>
  css({
    overflow: 'unset',
    marginBottom: 0,
    border: `1px solid ${hasError ? theme.colors.critical30 : theme.colors.neutral30}`,
    borderRadius: theme.borderRadius.small,
    '> div': {
      marginBottom: 0,
    },
    '> div > h3 > button': {
      border: 'none',
    },
    '> div > section > section': {
      border: 'none',
      borderTop: `1px solid ${theme.colors.neutral20}`,
    },
  });

type ScheduleMultiSelectDropdownChipProps = {
  value: string;
  id: string;
  onClear: (id: string) => void;
};

const ScheduleMultiSelectDropdownChip = ({ value, id, onClear }: ScheduleMultiSelectDropdownChipProps) => {
  const { tooltipProps, triggerProps, Tooltip } = useTooltip();
  return (
    <section css={scheduleMultiSelectDropdownChipStyles}>
      <Text {...triggerProps} css={chipTextStyles} size='small'>
        {value}
      </Text>
      <Icon
        name='x-small'
        css={{ width: theme.spacing(1), height: theme.spacing(1) }}
        color='light'
        onClick={(e) => {
          e.stopPropagation();
          onClear(id);
        }}
      />
      <Tooltip {...tooltipProps}>{value}</Tooltip>
    </section>
  );
};

type ScheduleMultiSelectDropdownOptionProps = {
  value: string;
  disabled?: boolean;
  children: ReactNode | string;
};

const ScheduleMultiSelectDropdownOption = ({ value, disabled, children }: ScheduleMultiSelectDropdownOptionProps) => {
  return (
    <ChecklistField.Option name={value} disabled={disabled}>
      {children}
    </ChecklistField.Option>
  );
};

ScheduleMultiSelectDropdown.Option = ScheduleMultiSelectDropdownOption;

const chipTextStyles = css({
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  color: theme.colors.neutral50,
  maxWidth: theme.spacing(8),
  textAlign: 'left',
});

const scheduleMultiSelectDropdownChipStyles = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  maxWidth: theme.spacing(16),
  minWidth: theme.spacing(10),
  background: theme.colors.neutral5,
  border: `1px solid ${theme.colors.neutral50}`,
  borderRadius: theme.borderRadius.small,
  padding: theme.spacing(0, 1),
});
